import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/shared/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

/**
 * Inline auth dialog: lets the user log in or register without leaving the page.
 * Calls onSuccess() after the session is established so the caller can continue.
 */
export default function AuthDialog({ open, onOpenChange, onSuccess }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#F9F7F4] border hairline rounded-none p-8" data-testid="auth-dialog">
        <DialogHeader className="space-y-2 text-left">
          <div className="label-xs text-[#ed6500]">
            {mode === "login" ? "Sign in to activate" : "Create account to activate"}
          </div>
          <DialogTitle className="font-serif font-light text-3xl leading-[0.95] tracking-tighter text-[#0A1128]">
            {mode === "login" ? "Welcome back." : "Start a plan."}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#515E7B]">
            We'll activate your route right after payment clears.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6 mt-4">
          {mode === "register" && (
            <div>
              <div className="label-xs mb-2">Full name</div>
              <input className="input-line" required value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name" />
            </div>
          )}
          <div>
            <div className="label-xs mb-2">Email</div>
            <input type="email" className="input-line" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-xs">Password</div>
              {mode === "login" && (
                <Link to="/forgot-password" onClick={() => onOpenChange(false)} className="text-[11px] text-[#ed6500] hover:underline" data-testid="auth-forgot-link">
                  Forgot password?
                </Link>
              )}
            </div>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                className="input-line pr-10"
                minLength={6} required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#515E7B] hover:text-[#0A1128] p-1"
                tabIndex={-1}
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="auth-password-toggle"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button disabled={busy} className="btn-primary w-full text-xs tracking-widest uppercase disabled:opacity-60" data-testid="auth-submit">
            {busy ? "…" : mode === "login" ? "Sign in & continue" : "Create account & continue"}
          </button>
        </form>

        <div className="mt-6 text-center label-xs text-[#515E7B]">
          {mode === "login" ? (
            <>New to Link? <button type="button" onClick={() => setMode("register")} className="text-[#ed6500] hover:underline" data-testid="auth-switch-register">Create account</button></>
          ) : (
            <>Already have an account? <button type="button" onClick={() => setMode("login")} className="text-[#ed6500] hover:underline" data-testid="auth-switch-login">Sign in</button></>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
